import { getEventPropsBuilder, toSnakeCase } from '../../utils';
import type { AmplitudeEventKeys } from '../../amplitude-properties.types';
import type { TrackingEvent } from '@/tracking/tracking-manager';

export type InteractionType = 'x_clicked' | 'outside_frame_clicked' | '';

export type TrackingData = {
    module: string | null;
    zoneName?: string | null;
    zoneLayout?: string | null;
    gameUid: string | null;
    gameName: string | null;
    gameProviderUid: string | null;
    linkText?: string | null;
    interactionType?: InteractionType | null;
};

type EventConfig = {
    event:
        | 'Games Exclusive Launch Modal Opened'
        | 'Games Exclusive Launch Modal Closed'
        | 'Games Exclusive Launch CTA Clicked';
    getModule: (data: TrackingData) => string;
    getZoneName?: (data: TrackingData) => string;
    getZoneLayout?: (data: TrackingData) => string;
    getGameID: (data: TrackingData) => string;
    getGameName: (data: TrackingData) => string;
    getGameProvider: (data: TrackingData) => string;
    getLinkText?: (data: TrackingData) => string;
    getInteractionType?: (data: TrackingData) => InteractionType;
};

const EVENT_CONFIGS: Record<string, EventConfig> = {
    gamesExclusiveLaunchModalOpened: {
        event: 'Games Exclusive Launch Modal Opened',
        getModule: (data) => toSnakeCase(data.module || ''),
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGameProvider: (data) => data.gameProviderUid || ''
    },
    gamesExclusiveLaunchModalClosed: {
        event: 'Games Exclusive Launch Modal Closed',
        getModule: (data) => toSnakeCase(data.module || ''),
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGameProvider: (data) => data.gameProviderUid || '',
        getInteractionType: (data) => data.interactionType || ''
    },
    gamesExclusiveLaunchCTAClicked: {
        event: 'Games Exclusive Launch CTA Clicked',
        getModule: (data) => toSnakeCase(data.module || ''),
        getZoneName: (data) => toSnakeCase(data.zoneName || ''),
        getZoneLayout: (data) => toSnakeCase(data.zoneLayout || ''),
        getGameID: (data) => toSnakeCase(data.gameUid || ''),
        getGameName: (data) => toSnakeCase(data.gameName || ''),
        getGameProvider: (data) => data.gameProviderUid || '',
        getLinkText: (data) => data.linkText || ''
    }
};

const buildEventProps = getEventPropsBuilder(
    (config: EventConfig, data: TrackingData | null): Partial<AmplitudeEventKeys> => {
        if (!data) {
            return {
                event: config.event
            };
        }

        const eventProps: Partial<AmplitudeEventKeys> = {
            event: config.event,
            Module: config.getModule(data),
            'Game ID': config.getGameID(data),
            'Game Name': config.getGameName(data),
            'Game Provider': config.getGameProvider(data)
        };

        const zoneName = config.getZoneName ? config.getZoneName(data) : null;
        if (zoneName) {
            eventProps['Zone Name'] = zoneName;
        }

        const zoneLayout = config.getZoneLayout ? config.getZoneLayout(data) : null;
        if (zoneLayout) {
            eventProps['Zone Layout'] = zoneLayout;
        }

        const linkText = config.getLinkText ? config.getLinkText(data) : null;
        if (linkText) {
            eventProps['Link Text'] = linkText;
        }

        const interactionType = config.getInteractionType ? config.getInteractionType(data) : null;
        if (interactionType) {
            eventProps['Interaction Type'] = interactionType;
        }

        return eventProps;
    }
);

export const extractTrackingData = (element: HTMLElement | null): TrackingData | null => {
    if (!element) {
        return null;
    }

    const game = element.closest('[data-t-gameuid]');
    const gameUid = game?.getAttribute('data-t-gameuid');

    let gameName = game?.getAttribute('data-t-gamename');
    if (!gameName) {
        gameName = element.closest('[data-t-gamename]')?.getAttribute('data-t-gamename');
    }

    let gameProviderUid = game?.getAttribute('data-t-gameprovideruid');
    if (!gameProviderUid) {
        gameProviderUid = element.closest('[data-t-gameprovideruid]')?.getAttribute('data-t-gameprovideruid');
    }

    const linkText = element.closest('[data-t-cta-button-text]')?.getAttribute('data-t-cta-button-text');

    const interactionType = element.closest('[data-t-interactiontype]')?.getAttribute('data-t-interactiontype');

    const exclusiveLaunch = element.closest('[data-t-prepostlaunchmodal]');
    const launchType = exclusiveLaunch?.getAttribute('data-t-prepostlaunchmodal');
    const zoneName = exclusiveLaunch?.getAttribute('data-t-zonename');
    const zoneLayout = exclusiveLaunch?.getAttribute('data-t-zonelayout');

    return {
        module: launchType || null,
        zoneName: zoneName || null,
        zoneLayout: zoneLayout || null,
        gameUid: gameUid || null,
        gameName: gameName || null,
        gameProviderUid: gameProviderUid || null,
        linkText: linkText || null,
        interactionType: (interactionType as InteractionType) || null
    };
};

export const trackExclusiveLaunchEvent = (
    eventType: 'gamesExclusiveLaunchModalOpened' | 'gamesExclusiveLaunchModalClosed' | 'gamesExclusiveLaunchCTAClicked',
    source?: { current: HTMLElement | null } | null,
    customData?: Partial<TrackingData>
): { event: TrackingEvent; trackingData: TrackingData | null } => {
    const config = EVENT_CONFIGS[eventType];
    if (!config) {
        console.warn(`[Tracking] Unknown exclusives event type: ${eventType}`);
    }

    let trackingData = source?.current ? extractTrackingData(source.current) : null;
    if (customData) {
        trackingData = { ...trackingData, ...customData } as TrackingData;
    }
    const eventProps = buildEventProps(config, trackingData);

    return {
        event: {
            destination: 'amplitude',
            eventProps
        },
        trackingData
    };
};
